import { withTransaction } from '@elastic/apm-rum-react'
import { Button, InputPassword, JessicaJonesService, LogService, ToastHelper } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import './new-password.page.scoped.scss'

interface Validation {
  newPassword: boolean
  passwordConfirmation: boolean
}

interface Fields {
  newPassword: string
  passwordConfirmation: State
}

interface State {
  value: string
  error: string
}

const LandingPageComponent = () => {
  const jessicaJones = useMemo(() => new JessicaJonesService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const navigate = useNavigate()
  logService.init('NewPassword')

  const [loadingButton, setLoadingButton] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [token, setToken] = useState<string | null>(null)
  const [validation, setValidation] = useState<Validation>({
    newPassword: false,
    passwordConfirmation: false,
  })
  const [state, setState] = useState<Fields>({
    newPassword: '',
    passwordConfirmation: {
      value: '',
      error: '',
    },
  })

  const { t } = useTranslation()
  const { search } = useLocation()

  const passwordConfirmationValidation = useCallback((password: string, confirmation: string): boolean => {
    return password === confirmation
  }, [])

  const validate = useCallback((): boolean => validation.newPassword && validation.passwordConfirmation, [validation])

  const handleConfirmation = (confirmation: string): void => {
    let error = ''
    if (!confirmation || confirmation?.length < 1) {
      error = t('newPasswordPage:required')
    } else if (!passwordConfirmationValidation(state.newPassword, confirmation)) {
      error = t('newPasswordPage:differentPasswords')
    }
    setState((prevState) => ({ ...prevState, passwordConfirmation: { value: confirmation, error } }))
    setValidation((prevValidation) => ({ ...prevValidation, passwordConfirmation: !error }))
  }

  const handleNewPassword = useCallback(
    (newPassword: string) => {
      let error = ''
      if (!passwordConfirmationValidation(newPassword, state.passwordConfirmation.value)) {
        error = t('newPasswordPage:differentPasswords')
      }
      setState((prevState) => ({
        ...prevState,
        newPassword,
        passwordConfirmation: {
          value: state.passwordConfirmation.value,
          error: prevState.passwordConfirmation.value === '' ? '' : error,
        },
      }))
      setValidation((prevValidation) => ({ ...prevValidation, passwordConfirmation: !error }))
    },
    [state.passwordConfirmation.value, passwordConfirmationValidation, t]
  )

  const submitHandle = async (event: any) => {
    event.preventDefault()
    try {
      setLoadingButton(true)
      await jessicaJones.resetPassword(token || '', state.newPassword)
      navigate('/login?login_message=password_redefined')
    } catch (err) {
      logService.error({ message: 'Error changing password', error: err })
      toast.error(t('newPasswordPage:tokenExpired'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        duration: 3600000,
        notOverClick: true,
        immediately: true,
      })
      navigate('/login/forgot-password')
    } finally {
      setLoadingButton(false)
    }
  }

  const hasTokenAndExpirationDateIsValid = useMemo(() => {
    const query = new URLSearchParams(search)
    const tokenParam = query.get('token')
    const expirationDate = query.get('expiry')

    if (tokenParam) {
      setToken(tokenParam)
    }

    const actualDateTimeStamp = Math.round(new Date().getTime() / 1000)

    return tokenParam && expirationDate && actualDateTimeStamp < Number(expirationDate)
  }, [search])

  useEffect(() => {
    setIsAuthenticated(history.state?.usr?.isAuthenticated)
  }, [])

  const tokenExpiredRedirect = useCallback(() => {
    toast.error(t('newPasswordPage:tokenExpired'), {
      closeButton: true,
      className: 'full text-bold',
      position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
      duration: 3600000,
      notOverClick: true,
      immediately: true,
    })
    return <Navigate to={'/login/forgot-password'} />
  }, [t, toast])

  const contentNewPassword = () => (
    <div>
      <h4 className="mb-5">{t('newPasswordPage:title')}</h4>
      <form onSubmit={submitHandle} style={{ width: '100%' }}>
        <InputPassword
          label={t('globalLogin:password')}
          value={state.newPassword}
          name="new-password"
          id="new-password"
          className="mb-3"
          placeholder="********"
          onChange={handleNewPassword}
          onValidate={(newPassword) => setValidation((prevValidation) => ({ ...prevValidation, newPassword }))}
          readOnly={loadingButton}
          autoComplete="current-password"
          showValidation
          required
        />
        <InputPassword
          label={t('newPasswordPage:passwordConfirmation')}
          value={state.passwordConfirmation.value}
          name="password-confirmation"
          id="password-confirmation"
          className="mb-3"
          placeholder="********"
          onChange={handleConfirmation}
          readOnly={loadingButton}
          externalError={(state.passwordConfirmation.error || validation.passwordConfirmation) && state.passwordConfirmation.error}
          autoComplete="current-password"
          required
        />
        <Button
          type="submit"
          disabled={!validate() || loadingButton}
          loading={loadingButton}
          className="button-register"
          size="xl"
          id="button-new-password"
          style={{ width: '100%' }}
        >
          {t('newPasswordPage:reset')}
        </Button>
        <p className="mt-5">
          <span>
            <strong>{t('newPasswordPage:notRegistered')}</strong>
          </span>
          <Link to="/login/register">{t('newPasswordPage:clickMe')}</Link>
        </p>
      </form>
    </div>
  )

  return hasTokenAndExpirationDateIsValid || isAuthenticated ? contentNewPassword() : tokenExpiredRedirect()
}

export default withTransaction('LandingPageComponent', 'component')(LandingPageComponent)
